import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button, Empty, Field, Icon, Input, Modal, Notice, Select, Table, type ColumnDef,
} from '../design/index.js'
import {
  usePlatformApi,
  type PlatformAdminView, type PlatformAuditView, type PlatformCompanyView,
  type PlatformTenantView, type PlatformUserView, type Role,
} from '../app/platform-api.js'
import { navigate, paths } from '../app/router.js'
import { ErrorBlock, Loading, messageFor } from '../framework/feedback.js'
import { formatDateTime } from '../framework/format.js'

/**
 * The platform console (migration 034).
 *
 * The tenant's own Administration pages answer "who is on this account". These
 * answer the question one level up: which accounts there are, what is inside
 * them, and who may sign in to any of them. Everything here is reached from an
 * identity that belongs to no tenant, so every screen names the tenant it is
 * acting on — there is no ambient "current company" to fall back on, and a
 * screen that forgot to say which customer it was changing would be dangerous.
 */

export const PLATFORM_SECTIONS = [
  { key: 'tenants', label: 'Tenants', blurb: 'Every account on this installation, and what is inside it.' },
  { key: 'people', label: 'People', blurb: 'Everybody in every tenant: add them, place them, take them out.' },
  { key: 'companies', label: 'Companies', blurb: 'The legal entities inside one tenant.' },
  { key: 'admins', label: 'Platform admins', blurb: 'Who else can reach this console.' },
  { key: 'audit', label: 'Trail', blurb: 'Every change made from here: who, what, which tenant, when.' },
] as const

const ROLE_OPTIONS = [
  { value: 'OWNER', label: 'Owner' },
  { value: 'ADMIN', label: 'Administrator' },
  { value: 'MEMBER', label: 'Member' },
]

const toggle = (list: readonly string[], id: string): string[] =>
  list.includes(id) ? list.filter((x) => x !== id) : [...list, id]

const when = (value: string | null): string => (value ? formatDateTime(value) : 'never')

/** The one-off password dialog, identical in wording wherever one is issued. */
const IssuedPassword = ({ who, password, onClose }: { who: string; password: string; onClose: () => void }) => (
  <Modal
    title="Their temporary password"
    tone="warn"
    onClose={onClose}
    actions={<Button kind="primary" onClick={onClose}>I have taken a note of it</Button>}
  >
    <p>
      Give this to <strong>{who}</strong>. It is shown once — nobody, including you, can read it back
      afterwards. They will be asked to choose their own when they sign in.
    </p>
    <p className="temp-password num">{password}</p>
  </Modal>
)

/* ---- tenants -------------------------------------------------------------- */

const Tenants = ({ onOpenCompanies }: { onOpenCompanies: (tenantId: string) => void }) => {
  const api = usePlatformApi()
  const client = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [issued, setIssued] = useState<{ who: string; password: string } | null>(null)
  const [draft, setDraft] = useState({
    slug: '', name: '', companyName: '', abbreviation: '', ownerName: '', ownerEmail: '',
  })

  const tenants = useQuery({ queryKey: ['platformTenants'], queryFn: () => api.tenants() })
  const settle = (next: readonly PlatformTenantView[]): void => {
    client.setQueryData(['platformTenants'], next)
    setError(null)
  }

  const create = useMutation({
    mutationFn: () => api.createTenant(draft),
    onSuccess: (result) => {
      setAdding(false)
      setDraft({ slug: '', name: '', companyName: '', abbreviation: '', ownerName: '', ownerEmail: '' })
      setIssued({ who: `${result.owner.name} <${result.owner.email}>`, password: result.temporaryPassword })
      void client.invalidateQueries({ queryKey: ['platformTenants'] })
      void client.invalidateQueries({ queryKey: ['platformUsers'] })
    },
    onError: (err) => setError(messageFor(err)),
  })

  const setActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => api.setTenantActive(id, active),
    onSuccess: settle,
    onError: (err) => setError(messageFor(err)),
  })

  if (tenants.isLoading) return <Loading />
  if (tenants.error) return <ErrorBlock error={tenants.error} />

  const columns: ColumnDef<PlatformTenantView>[] = [
    { key: 'name', header: 'Tenant',
      render: (row) => (
        <>
          <div>{row.name}</div>
          <div className="cell-sub num">{row.slug} · {row.schemaName}</div>
        </>
      ) },
    { key: 'companyCount', header: 'Companies', width: 110,
      render: (row) => <span className="num">{row.companyCount}</span> },
    { key: 'userCount', header: 'People', width: 130,
      render: (row) => (
        <>
          <span className="num">{row.activeUserCount}</span>
          {row.userCount !== row.activeUserCount
            ? <div className="cell-sub">{row.userCount - row.activeUserCount} suspended</div>
            : null}
        </>
      ) },
    { key: 'active', header: 'Status', width: 140,
      render: (row) => (row.active ? 'Active' : <strong>Suspended</strong>) },
    { key: 'actions', header: '', width: 260,
      render: (row) => (
        <span style={{ display: 'inline-flex', gap: 8 }}>
          <Button kind="ghost" onClick={() => onOpenCompanies(row.id)}>Companies</Button>
          <Button
            kind="ghost"
            disabled={setActive.isPending}
            onClick={() => setActive.mutate({ id: row.id, active: !row.active })}
          >
            {row.active ? 'Suspend' : 'Restore'}
          </Button>
        </span>
      ) },
  ]

  const ready = draft.slug.trim().length >= 2 && draft.name.trim().length >= 2
    && draft.companyName.trim().length >= 2 && draft.abbreviation.trim().length >= 2
    && draft.ownerName.trim().length >= 2 && draft.ownerEmail.includes('@')

  return (
    <>
      <div className="console-toolbar">
        <Button kind="primary" icon={<Icon name="plus" size={18} />} onClick={() => setAdding(true)}>
          New tenant
        </Button>
      </div>

      {error ? <div style={{ marginBottom: 16 }}><Notice kind="bad">{error}</Notice></div> : null}

      <Notice kind="info">
        Suspending a tenant signs everybody in it out at once and refuses every sign-in until it is
        restored. Nothing inside it is deleted.
      </Notice>

      <div style={{ marginTop: 18 }}>
        <Table columns={columns} rows={[...(tenants.data ?? [])]} getKey={(row) => row.id} />
      </div>

      {adding ? (
        <Modal
          title="Create a tenant"
          onClose={() => setAdding(false)}
          actions={
            <>
              <Button kind="ghost" onClick={() => setAdding(false)}>Cancel</Button>
              <Button kind="primary" disabled={create.isPending || !ready} onClick={() => create.mutate()}>
                {create.isPending ? 'Creating…' : 'Create it'}
              </Button>
            </>
          }
        >
          <Field
            label="Slug"
            required
            hint="Lowercase letters, digits and underscores. It becomes the database schema and cannot be changed afterwards."
          >
            <Input value={draft.slug} onChange={(slug) => setDraft({ ...draft, slug })} />
          </Field>
          <Field label="Tenant name" required hint="What this customer is called.">
            <Input value={draft.name} onChange={(name) => setDraft({ ...draft, name })} />
          </Field>

          <h3 className="subhead" style={{ marginTop: 20 }}>Its first company</h3>
          <Field label="Company name" required>
            <Input value={draft.companyName} onChange={(companyName) => setDraft({ ...draft, companyName })} />
          </Field>
          <Field label="Abbreviation" required hint="Two to ten characters. It prefixes document numbers.">
            <Input value={draft.abbreviation} onChange={(abbreviation) => setDraft({ ...draft, abbreviation })} />
          </Field>

          <h3 className="subhead" style={{ marginTop: 20 }}>Its first owner</h3>
          <Field label="Name" required>
            <Input value={draft.ownerName} onChange={(ownerName) => setDraft({ ...draft, ownerName })} />
          </Field>
          <Field label="Email address" required hint="They sign in with this, at the ordinary sign-in page.">
            <Input value={draft.ownerEmail} onChange={(ownerEmail) => setDraft({ ...draft, ownerEmail })} />
          </Field>
          <p className="muted">
            This creates the tenant&rsquo;s database schema, its first company and its first owner, and
            hands you a temporary password for them. Everything else the customer sets up themselves.
          </p>
        </Modal>
      ) : null}

      {issued ? <IssuedPassword who={issued.who} password={issued.password} onClose={() => setIssued(null)} /> : null}
    </>
  )
}

/* ---- a tenant picker, used by three screens ------------------------------- */

const TenantPicker = ({
  tenants, value, onChange, label = 'Tenant', allowAll = false,
}: {
  tenants: readonly PlatformTenantView[]
  value: string
  onChange: (id: string) => void
  label?: string
  allowAll?: boolean
}) => (
  <Field label={label}>
    <Select
      value={value}
      onChange={onChange}
      options={[
        ...(allowAll ? [{ value: '', label: 'Every tenant' }] : []),
        ...tenants.map((t) => ({ value: t.id, label: t.active ? t.name : `${t.name} (suspended)` })),
      ]}
    />
  </Field>
)

/* ---- companies ------------------------------------------------------------ */

const emptyCompany = { name: '', legalName: '', abbreviation: '', gstin: '', active: true }

const Companies = ({ tenantId, onTenant }: { tenantId: string; onTenant: (id: string) => void }) => {
  const api = usePlatformApi()
  const client = useQueryClient()
  const [editing, setEditing] = useState<null | (typeof emptyCompany & { id?: string })>(null)
  const [error, setError] = useState<string | null>(null)

  const tenants = useQuery({ queryKey: ['platformTenants'], queryFn: () => api.tenants() })
  const companies = useQuery({
    queryKey: ['platformCompanies', tenantId],
    queryFn: () => api.companies(tenantId),
    enabled: tenantId !== '',
  })

  const save = useMutation({
    mutationFn: () => api.saveCompany(tenantId, {
      ...(editing?.id ? { id: editing.id } : {}),
      name: editing!.name,
      legalName: editing!.legalName,
      abbreviation: editing!.abbreviation,
      gstin: editing!.gstin.trim() === '' ? null : editing!.gstin.trim(),
      active: editing!.active,
    }),
    onSuccess: (next) => {
      setEditing(null)
      setError(null)
      client.setQueryData(['platformCompanies', tenantId], next)
      void client.invalidateQueries({ queryKey: ['platformTenants'] })
    },
    onError: (err) => setError(messageFor(err)),
  })

  const all = tenants.data ?? []
  if (tenants.isLoading) return <Loading />
  if (all.length === 0) return <Empty title="There are no tenants yet." body="Create one first." />

  const columns: ColumnDef<PlatformCompanyView>[] = [
    { key: 'name', header: 'Company',
      render: (row) => (
        <>
          <div>{row.name}</div>
          <div className="cell-sub">{row.legalName}</div>
        </>
      ) },
    { key: 'abbreviation', header: 'Abbreviation', width: 140,
      render: (row) => <span className="num">{row.abbreviation}</span> },
    { key: 'gstin', header: 'GSTIN', width: 200,
      render: (row) => <span className="num">{row.gstin ?? '—'}</span> },
    { key: 'active', header: 'Status', width: 120, render: (row) => (row.active ? 'Active' : 'Inactive') },
    { key: 'actions', header: '', width: 120,
      render: (row) => (
        <Button
          kind="ghost"
          onClick={() => setEditing({
            id: row.id, name: row.name, legalName: row.legalName,
            abbreviation: row.abbreviation, gstin: row.gstin ?? '', active: row.active,
          })}
        >
          Edit
        </Button>
      ) },
  ]

  return (
    <>
      <div className="console-toolbar">
        <div className="console-toolbar__field">
          <TenantPicker tenants={all} value={tenantId} onChange={onTenant} />
        </div>
        <Button
          kind="primary"
          icon={<Icon name="plus" size={18} />}
          disabled={tenantId === ''}
          onClick={() => setEditing({ ...emptyCompany })}
        >
          Add a company
        </Button>
      </div>

      {error ? <div style={{ marginBottom: 16 }}><Notice kind="bad">{error}</Notice></div> : null}

      {tenantId === '' ? (
        <Empty title="Choose a tenant." body="Companies live inside one tenant, so one has to be named." />
      ) : companies.isLoading ? <Loading />
        : companies.error ? <ErrorBlock error={companies.error} />
        : <Table columns={columns} rows={[...(companies.data ?? [])]} getKey={(row) => row.id} />}

      {editing ? (
        <Modal
          title={editing.id ? 'Edit this company' : 'Add a company'}
          onClose={() => setEditing(null)}
          actions={
            <>
              <Button kind="ghost" onClick={() => setEditing(null)}>Cancel</Button>
              <Button
                kind="primary"
                disabled={save.isPending || editing.name.trim().length < 2 || editing.abbreviation.trim().length < 2}
                onClick={() => save.mutate()}
              >
                Save
              </Button>
            </>
          }
        >
          <Field label="Name" required>
            <Input value={editing.name} onChange={(name) => setEditing({ ...editing, name })} />
          </Field>
          <Field label="Legal name" hint="What appears on printed documents. Defaults to the name.">
            <Input value={editing.legalName} onChange={(legalName) => setEditing({ ...editing, legalName })} />
          </Field>
          <Field label="Abbreviation" required hint="Two to ten characters. It prefixes document numbers.">
            <Input value={editing.abbreviation} onChange={(abbreviation) => setEditing({ ...editing, abbreviation })} />
          </Field>
          <Field label="GSTIN">
            <Input value={editing.gstin} onChange={(gstin) => setEditing({ ...editing, gstin })} />
          </Field>
          <Field label="Status">
            <Select
              value={editing.active ? 'yes' : 'no'}
              onChange={(v) => setEditing({ ...editing, active: v === 'yes' })}
              options={[{ value: 'yes', label: 'Active' }, { value: 'no', label: 'Inactive' }]}
            />
          </Field>
        </Modal>
      ) : null}
    </>
  )
}

/* ---- people --------------------------------------------------------------- */

const CompanyGrantDialog = ({
  user, onClose,
}: { user: PlatformUserView; onClose: () => void }) => {
  const api = usePlatformApi()
  const client = useQueryClient()
  const [chosen, setChosen] = useState<readonly string[]>(user.companyIds)
  const [error, setError] = useState<string | null>(null)

  const companies = useQuery({
    queryKey: ['platformCompanies', user.tenantId],
    queryFn: () => api.companies(user.tenantId),
  })

  const save = useMutation({
    mutationFn: () => api.setUserCompanies(user.id, chosen),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['platformUsers'] })
      onClose()
    },
    onError: (err) => setError(messageFor(err)),
  })

  return (
    <Modal
      title={`Which companies ${user.name} may reach`}
      onClose={onClose}
      actions={
        <>
          <Button kind="ghost" onClick={onClose}>Cancel</Button>
          <Button kind="primary" disabled={save.isPending} onClick={() => save.mutate()}>Save</Button>
        </>
      }
    >
      {error ? <div style={{ marginBottom: 14 }}><Notice kind="bad">{error}</Notice></div> : null}
      <p className="muted">
        In <strong>{user.tenantName}</strong>. Ticking none is not &ldquo;no companies&rdquo; — it is every
        company in the tenant, including ones added later. Tick some to pin them to exactly those.
      </p>
      {companies.isLoading ? <Loading /> : (
        <div className="column-choices" style={{ marginTop: 14 }}>
          {(companies.data ?? []).map((c) => (
            <label className="column-choice" key={c.id}>
              <input
                type="checkbox"
                checked={chosen.includes(c.id)}
                onChange={() => setChosen(toggle(chosen, c.id))}
              />
              <span>{c.name}{c.active ? '' : ' (inactive)'}</span>
            </label>
          ))}
        </div>
      )}
    </Modal>
  )
}

const People = ({ tenantId, onTenant }: { tenantId: string; onTenant: (id: string) => void }) => {
  const api = usePlatformApi()
  const client = useQueryClient()
  const [term, setTerm] = useState('')
  const [adding, setAdding] = useState(false)
  const [grantFor, setGrantFor] = useState<PlatformUserView | null>(null)
  const [issued, setIssued] = useState<{ who: string; password: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [draft, setDraft] = useState<{
    tenantId: string; name: string; email: string; role: Role; companyIds: readonly string[]
  }>({ tenantId: '', name: '', email: '', role: 'MEMBER', companyIds: [] })

  const tenants = useQuery({ queryKey: ['platformTenants'], queryFn: () => api.tenants() })
  const users = useQuery({
    queryKey: ['platformUsers', tenantId, term],
    queryFn: () => api.users({ ...(tenantId ? { tenantId } : {}), ...(term ? { q: term } : {}) }),
  })
  const draftCompanies = useQuery({
    queryKey: ['platformCompanies', draft.tenantId],
    queryFn: () => api.companies(draft.tenantId),
    enabled: adding && draft.tenantId !== '',
  })

  // The server answers with one tenant's people; the list on screen may span
  // several, so it is refetched rather than patched from a partial reply.
  const settle = (): void => {
    void client.invalidateQueries({ queryKey: ['platformUsers'] })
    setError(null)
  }

  const add = useMutation({
    mutationFn: () => api.createUser({
      tenantId: draft.tenantId, name: draft.name, email: draft.email,
      role: draft.role, companyIds: draft.companyIds,
    }),
    onSuccess: (result) => {
      setAdding(false)
      setDraft({ tenantId: draft.tenantId, name: '', email: '', role: 'MEMBER', companyIds: [] })
      setIssued({ who: `${result.user.name} <${result.user.email}>`, password: result.temporaryPassword })
      void client.invalidateQueries({ queryKey: ['platformUsers'] })
      void client.invalidateQueries({ queryKey: ['platformTenants'] })
    },
    onError: (err) => setError(messageFor(err)),
  })

  const setRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: Role }) => api.setUserRole(id, role),
    onSuccess: settle,
    onError: (err) => setError(messageFor(err)),
  })

  const setActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => api.setUserActive(id, active),
    onSuccess: settle,
    onError: (err) => setError(messageFor(err)),
  })

  const reset = useMutation({
    mutationFn: (user: PlatformUserView) => api.resetUserPassword(user.id).then((r) => ({ user, ...r })),
    onSuccess: (result) => {
      setIssued({ who: `${result.user.name} <${result.user.email}>`, password: result.temporaryPassword })
      void client.invalidateQueries({ queryKey: ['platformUsers'] })
    },
    onError: (err) => setError(messageFor(err)),
  })

  const all = tenants.data ?? []

  const columns: ColumnDef<PlatformUserView>[] = [
    { key: 'name', header: 'Name',
      render: (row) => (
        <>
          <div>{row.name}</div>
          <div className="cell-sub">{row.email}</div>
        </>
      ) },
    { key: 'tenantName', header: 'Tenant', width: 190,
      render: (row) => (
        <>
          <div>{row.tenantName}</div>
          {row.tenantActive ? null : <div className="cell-sub">Tenant suspended</div>}
        </>
      ) },
    { key: 'companyNames', header: 'Companies', width: 220,
      render: (row) => (
        <>
          <div>{row.companyNames.length === 0 ? '—' : row.companyNames.join(', ')}</div>
          {row.companyIds.length === 0 ? <div className="cell-sub">every company</div> : null}
        </>
      ) },
    { key: 'role', header: 'Role', width: 180,
      render: (row) => (
        <Select
          value={row.role}
          onChange={(role) => setRole.mutate({ id: row.id, role: role as Role })}
          options={ROLE_OPTIONS}
          aria-label={`Role for ${row.name}`}
        />
      ) },
    { key: 'active', header: 'Account', width: 170,
      render: (row) => (
        <>
          <div>{row.active ? 'Active' : 'Suspended'}</div>
          {row.mustChangePassword ? <div className="cell-sub">Temporary password</div> : null}
          {row.lockedUntil ? <div className="cell-sub">Locked after failed sign-ins</div> : null}
        </>
      ) },
    { key: 'actions', header: '', width: 320,
      render: (row) => (
        <span style={{ display: 'inline-flex', gap: 8 }}>
          <Button kind="ghost" onClick={() => setGrantFor(row)}>Companies</Button>
          <Button kind="ghost" disabled={reset.isPending} onClick={() => reset.mutate(row)}>Reset password</Button>
          <Button
            kind="ghost"
            disabled={setActive.isPending}
            onClick={() => setActive.mutate({ id: row.id, active: !row.active })}
          >
            {row.active ? 'Suspend' : 'Restore'}
          </Button>
        </span>
      ) },
  ]

  const ready = draft.tenantId !== '' && draft.name.trim().length >= 2 && draft.email.includes('@')

  return (
    <>
      <div className="console-toolbar">
        <div className="console-toolbar__field">
          <TenantPicker tenants={all} value={tenantId} onChange={onTenant} allowAll />
        </div>
        <div className="console-toolbar__field">
          <Field label="Search" hint="Name or email address.">
            <Input value={term} onChange={setTerm} />
          </Field>
        </div>
        <Button
          kind="primary"
          icon={<Icon name="plus" size={18} />}
          disabled={all.length === 0}
          onClick={() => {
            setDraft({ ...draft, tenantId: tenantId || all[0]?.id || '', companyIds: [] })
            setAdding(true)
          }}
        >
          Add someone
        </Button>
      </div>

      {error ? <div style={{ marginBottom: 16 }}><Notice kind="bad">{error}</Notice></div> : null}

      {users.isLoading ? <Loading />
        : users.error ? <ErrorBlock error={users.error} />
        : (users.data ?? []).length === 0
          ? <Empty title="Nobody matches that." body="Clear the search, or choose a different tenant." />
          : <Table columns={columns} rows={[...(users.data ?? [])]} getKey={(row) => row.id} />}

      {adding ? (
        <Modal
          title="Add someone to a tenant"
          onClose={() => setAdding(false)}
          actions={
            <>
              <Button kind="ghost" onClick={() => setAdding(false)}>Cancel</Button>
              <Button kind="primary" disabled={add.isPending || !ready} onClick={() => add.mutate()}>
                Add them
              </Button>
            </>
          }
        >
          <TenantPicker
            tenants={all}
            value={draft.tenantId}
            onChange={(id) => setDraft({ ...draft, tenantId: id, companyIds: [] })}
            label="Which tenant"
          />
          <Field label="Name" required>
            <Input value={draft.name} onChange={(name) => setDraft({ ...draft, name })} />
          </Field>
          <Field label="Email address" required hint="They sign in with this. It must be unused in that tenant.">
            <Input value={draft.email} onChange={(email) => setDraft({ ...draft, email })} />
          </Field>
          <Field label="Role" required>
            <Select
              value={draft.role}
              onChange={(role) => setDraft({ ...draft, role: role as Role })}
              options={ROLE_OPTIONS}
            />
          </Field>

          <h3 className="subhead" style={{ marginTop: 20 }}>Which companies</h3>
          <p className="muted">
            Tick none for every company in the tenant, including ones added later.
          </p>
          {draftCompanies.isLoading ? <Loading /> : (
            <div className="column-choices" style={{ marginTop: 10 }}>
              {(draftCompanies.data ?? []).map((c) => (
                <label className="column-choice" key={c.id}>
                  <input
                    type="checkbox"
                    checked={draft.companyIds.includes(c.id)}
                    onChange={() => setDraft({ ...draft, companyIds: toggle(draft.companyIds, c.id) })}
                  />
                  <span>{c.name}</span>
                </label>
              ))}
            </div>
          )}
          <p className="muted" style={{ marginTop: 14 }}>
            A temporary password is made for them and shown to you once. They choose their own when they
            first sign in.
          </p>
        </Modal>
      ) : null}

      {grantFor ? <CompanyGrantDialog user={grantFor} onClose={() => setGrantFor(null)} /> : null}
      {issued ? <IssuedPassword who={issued.who} password={issued.password} onClose={() => setIssued(null)} /> : null}
    </>
  )
}

/* ---- platform admins ------------------------------------------------------- */

const Admins = ({ adminId }: { adminId: string }) => {
  const api = usePlatformApi()
  const client = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState({ name: '', email: '' })
  const [issued, setIssued] = useState<{ who: string; password: string } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const admins = useQuery({ queryKey: ['platformAdmins'], queryFn: () => api.admins() })
  const settle = (next: readonly PlatformAdminView[]): void => {
    client.setQueryData(['platformAdmins'], next)
    setError(null)
  }

  const add = useMutation({
    mutationFn: () => api.createAdmin(draft.name, draft.email),
    onSuccess: (result) => {
      setAdding(false)
      setDraft({ name: '', email: '' })
      setIssued({ who: `${result.admin.name} <${result.admin.email}>`, password: result.temporaryPassword })
      void client.invalidateQueries({ queryKey: ['platformAdmins'] })
    },
    onError: (err) => setError(messageFor(err)),
  })

  const setActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => api.setAdminActive(id, active),
    onSuccess: settle,
    onError: (err) => setError(messageFor(err)),
  })

  if (admins.isLoading) return <Loading />
  if (admins.error) return <ErrorBlock error={admins.error} />

  const columns: ColumnDef<PlatformAdminView>[] = [
    { key: 'name', header: 'Name',
      render: (row) => (
        <>
          <div>{row.name}{row.id === adminId ? ' (you)' : ''}</div>
          <div className="cell-sub">{row.email}</div>
        </>
      ) },
    { key: 'lastLoginAt', header: 'Last signed in', width: 200,
      render: (row) => <span className="num">{when(row.lastLoginAt)}</span> },
    { key: 'active', header: 'Account', width: 200,
      render: (row) => (
        <>
          <div>{row.active ? 'Active' : 'Suspended'}</div>
          {row.mustChangePassword ? <div className="cell-sub">Temporary password</div> : null}
          {row.lockedUntil ? <div className="cell-sub">Locked after failed sign-ins</div> : null}
        </>
      ) },
    { key: 'actions', header: '', width: 140,
      render: (row) => (
        <Button
          kind="ghost"
          disabled={setActive.isPending || row.id === adminId}
          onClick={() => setActive.mutate({ id: row.id, active: !row.active })}
        >
          {row.active ? 'Suspend' : 'Restore'}
        </Button>
      ) },
  ]

  return (
    <>
      <div className="console-toolbar">
        <Button kind="primary" icon={<Icon name="plus" size={18} />} onClick={() => setAdding(true)}>
          Add a platform admin
        </Button>
      </div>

      {error ? <div style={{ marginBottom: 16 }}><Notice kind="bad">{error}</Notice></div> : null}

      <Notice kind="warn">
        A platform admin reaches every tenant on this installation. Add one only for somebody who
        already administers the deployment itself.
      </Notice>

      <div style={{ marginTop: 18 }}>
        <Table columns={columns} rows={[...(admins.data ?? [])]} getKey={(row) => row.id} />
      </div>

      {adding ? (
        <Modal
          title="Add a platform admin"
          tone="warn"
          onClose={() => setAdding(false)}
          actions={
            <>
              <Button kind="ghost" onClick={() => setAdding(false)}>Cancel</Button>
              <Button
                kind="primary"
                disabled={add.isPending || draft.name.trim().length < 2 || !draft.email.includes('@')}
                onClick={() => add.mutate()}
              >
                Add them
              </Button>
            </>
          }
        >
          <Field label="Name" required>
            <Input value={draft.name} onChange={(name) => setDraft({ ...draft, name })} />
          </Field>
          <Field label="Email address" required hint="They sign in here, not at the ordinary sign-in page.">
            <Input value={draft.email} onChange={(email) => setDraft({ ...draft, email })} />
          </Field>
        </Modal>
      ) : null}

      {issued ? <IssuedPassword who={issued.who} password={issued.password} onClose={() => setIssued(null)} /> : null}
    </>
  )
}

/* ---- the trail -------------------------------------------------------------- */

const describe = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

const Trail = ({ tenantId, onTenant }: { tenantId: string; onTenant: (id: string) => void }) => {
  const api = usePlatformApi()
  const tenants = useQuery({ queryKey: ['platformTenants'], queryFn: () => api.tenants() })
  const trail = useQuery({
    queryKey: ['platformAudit', tenantId],
    queryFn: () => api.audit({ ...(tenantId ? { tenantId } : {}), limit: 200 }),
  })

  const columns: ColumnDef<PlatformAuditView>[] = [
    { key: 'at', header: 'When', width: 190, render: (row) => <span className="num">{formatDateTime(row.at)}</span> },
    { key: 'adminEmail', header: 'Who', width: 220 },
    { key: 'tenantSlug', header: 'Tenant', width: 140,
      render: (row) => <span className="num">{row.tenantSlug ?? '—'}</span> },
    { key: 'action', header: 'What', width: 180,
      render: (row) => <>{row.entity} · {row.action}</> },
    { key: 'after', header: 'Detail',
      render: (row) => (
        <>
          {row.before ? <div className="cell-sub">was {describe(row.before)}</div> : null}
          <div>{describe(row.after) || '—'}</div>
        </>
      ) },
  ]

  return (
    <>
      <div className="console-toolbar">
        <div className="console-toolbar__field">
          <TenantPicker tenants={tenants.data ?? []} value={tenantId} onChange={onTenant} allowAll label="Filter by tenant" />
        </div>
      </div>
      {trail.isLoading ? <Loading />
        : trail.error ? <ErrorBlock error={trail.error} />
        : (trail.data ?? []).length === 0
          ? <Empty title="Nothing has been done from this console yet." body="Every change made here is recorded." />
          : <Table columns={columns} rows={[...(trail.data ?? [])]} getKey={(row) => row.id} />}
    </>
  )
}

/* ---- own password ------------------------------------------------------------ */

export const PlatformPasswordPage = ({ onChanged }: { onChanged: () => void }) => {
  const api = usePlatformApi()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [again, setAgain] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const change = useMutation({
    mutationFn: () => api.changePassword(current, next),
    onSuccess: () => {
      setDone(true)
      setError(null)
      onChanged()
    },
    onError: (err) => setError(messageFor(err)),
  })

  const mismatch = again !== '' && next !== again

  return (
    <div className="page page--narrow">
      <h1 className="page-title">Your password</h1>
      <p className="page-subtitle">
        Changing it signs out every other session this account has, here and nowhere else.
      </p>

      {done ? <div style={{ marginTop: 20 }}><Notice kind="ok">Your password has been changed.</Notice></div> : null}
      {error ? <div style={{ marginTop: 20 }}><Notice kind="bad">{error}</Notice></div> : null}

      <div style={{ marginTop: 24, maxWidth: 420 }}>
        <Field label="Your current password" required>
          <Input value={current} onChange={setCurrent} type="password" autoComplete="current-password" />
        </Field>
        <Field
          label="New password"
          required
          hint="At least eight characters, with an uppercase letter, a lowercase letter, a number and a symbol."
        >
          <Input value={next} onChange={setNext} type="password" autoComplete="new-password" />
        </Field>
        <Field label="New password again" required {...(mismatch ? { error: 'The two do not match' } : {})}>
          <Input value={again} onChange={setAgain} type="password" autoComplete="new-password" />
        </Field>
        <Button
          kind="primary"
          disabled={change.isPending || current === '' || next === '' || mismatch}
          onClick={() => change.mutate()}
        >
          Change it
        </Button>
      </div>
    </div>
  )
}

/* ---- the section switch --------------------------------------------------- */

export const PlatformSection = ({
  section, adminId, tenantId, onTenant,
}: {
  section: string
  adminId: string
  /** Held above this component: it is remounted on every section change. */
  tenantId: string
  onTenant: (id: string) => void
}) => {
  const known = useMemo(() => PLATFORM_SECTIONS.find((s) => s.key === (section || 'tenants')), [section])

  if (!known) {
    return (
      <div className="page page--narrow">
        <h1 className="page-title">Not found</h1>
        <p className="page-subtitle">There is no platform page at &ldquo;{section}&rdquo;.</p>
      </div>
    )
  }

  return (
    <div className="page">
      <h1 className="page-title">{known.label}</h1>
      <p className="page-subtitle">{known.blurb}</p>
      {known.key === 'tenants' ? <Tenants onOpenCompanies={(id) => {
        onTenant(id)
        navigate(paths.platform('companies'))
      }} /> : null}
      {known.key === 'people' ? <People tenantId={tenantId} onTenant={onTenant} /> : null}
      {known.key === 'companies' ? <Companies tenantId={tenantId} onTenant={onTenant} /> : null}
      {known.key === 'admins' ? <Admins adminId={adminId} /> : null}
      {known.key === 'audit' ? <Trail tenantId={tenantId} onTenant={onTenant} /> : null}
    </div>
  )
}
